// Un informe (con el diseño de Venuz, en su columna de lectura de 760 px) o un texto, dentro de la hoja de vista previa.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Informe } from '../informe/Informe';
import { Cargando, NoSePudo, type ControlCuerpo, type PropsCuerpo } from './piezas';
import { imagenDeInforme } from './url';

type Texto = { estado: 'cargando' } | { estado: 'error' } | { estado: 'listo'; texto: string };

function useTexto(url: string | null): Texto {
  const [r, setR] = useState<Texto>({ estado: 'cargando' });
  useEffect(() => {
    if (!url) return;
    let vivo = true;
    setR({ estado: 'cargando' });
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const texto = await res.text();
        if (vivo) setR({ estado: 'listo', texto });
      })
      .catch(() => vivo && setR({ estado: 'error' }));
    return () => {
      vivo = false;
    };
  }, [url]);
  return r;
}

export const VisorTexto = forwardRef<ControlCuerpo, PropsCuerpo>(function VisorTexto({ elemento }, ref) {
  const zona = useRef<HTMLDivElement>(null);
  // Lo que no es texto (un .zip, un .docx) no se puede ver aquí: queda «Descargar» y «Abrir en otra pestaña».
  const binario = elemento.clase === 'otro';
  const contenido = useTexto(binario ? null : elemento.url);
  useImperativeHandle(ref, () => ({
    anterior: () => zona.current?.scrollBy({ top: -(zona.current.clientHeight * 0.85) }),
    siguiente: () => zona.current?.scrollBy({ top: zona.current.clientHeight * 0.85 }),
    inicio: () => zona.current?.scrollTo({ top: 0 }),
    fin: () => zona.current?.scrollTo({ top: zona.current.scrollHeight }),
    ajuste: () => 100,
    desplazable: () => zona.current,
  }));
  return (
    <div ref={zona} data-autofoco tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto bg-oficina-fondo outline-none">
      {binario || contenido.estado === 'error' ? (
        <NoSePudo />
      ) : contenido.estado === 'cargando' ? (
        <Cargando />
      ) : elemento.clase === 'informe' ? (
        // La columna de lectura (760 px) del informe, con su diseño de Venuz.
        <div className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
          <Informe fuente={contenido.texto} imagen={(src) => (elemento.agentId ? imagenDeInforme(elemento.agentId, src) : null)} />
        </div>
      ) : (
        <pre className="mx-auto max-w-3xl whitespace-pre-wrap break-words px-6 py-8 font-mono text-o-sub text-oficina-texto2">{contenido.texto}</pre>
      )}
    </div>
  );
});
